import { parseDbTimestamp } from "@repo/shared";
import type { IdentityChangeEntry, IdentityChangeSourceName } from "@repo/shared/types";
import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { useNavigate } from "react-router";
import { PageHeader } from "@/components/page-header";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { getIdentityChanges } from "@/lib/api/compliance";
import { useMe } from "@/lib/hooks/use-clients";
import { useT } from "@/lib/i18n/use-t";
import { hasPermission, type Permission } from "@/lib/permissions";
import { useRegionalFormat } from "@/lib/regional-format/context";
import { T } from "./page.i18n";

/** The sources with no signed-in user behind them. */
const UNATTENDED: IdentityChangeSourceName[] = ["sumsub_public", "eid_login", "public_api"];

function ChangeRow({ change }: { change: IdentityChangeEntry }) {
  const t = useT(T);
  const { formatDateTime } = useRegionalFormat();
  const unattended = UNATTENDED.includes(change.source);

  return (
    <TableRow>
      <TableCell className="whitespace-nowrap text-muted-foreground text-xs">
        {formatDateTime(parseDbTimestamp(change.changedAt))}
      </TableCell>
      <TableCell>
        <div className="flex flex-col">
          <span className="font-medium text-sm">{change.clientName ?? "—"}</span>
          {/* Whose identity changed. A representative's name and the company
              they act for are different facts, and a log that blurred them
              would be worse than none. */}
          {change.subjectKind === "representative" && (
            <span className="text-muted-foreground text-xs">
              {t.via(change.subjectName ?? t.representative)}
            </span>
          )}
        </div>
      </TableCell>
      <TableCell className="text-sm">{t.fields[change.field] ?? change.field}</TableCell>
      <TableCell className="text-muted-foreground text-sm line-through">
        {change.oldValue || "—"}
      </TableCell>
      <TableCell className="font-medium text-sm">{change.newValue || "—"}</TableCell>
      <TableCell>
        <Badge variant={unattended ? "warning" : "secondary"}>
          {t.sources[change.source] ?? change.source}
        </Badge>
      </TableCell>
      <TableCell className="text-sm">
        {change.changedByName ?? (
          // Null is the honest answer, not missing data: nobody was signed in.
          <span className="text-muted-foreground italic">{t.notSignedIn}</span>
        )}
      </TableCell>
    </TableRow>
  );
}

export default function ComplianceIdentityChangesPage() {
  const t = useT(T);
  const navigate = useNavigate();
  const { me, isLoading: meLoading } = useMe();
  const userPermissions = (me?.permissions || ["advisor"]) as Permission[];

  useEffect(() => {
    if (!meLoading && !hasPermission(userPermissions, "compliance")) {
      navigate("/", { replace: true });
    }
  }, [meLoading, userPermissions, navigate]);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["compliance", "identity-changes"],
    queryFn: () => getIdentityChanges(),
    enabled: !meLoading && hasPermission(userPermissions, "compliance"),
  });

  if (meLoading || !hasPermission(userPermissions, "compliance")) return null;

  const changes = data?.changes ?? [];

  return (
    <div className="space-y-6">
      <PageHeader title={t.title} description={t.description} />

      {isLoading ? (
        <p className="text-muted-foreground text-sm">{t.loading}</p>
      ) : isError ? (
        // A failed fetch used to fall through to "No identity changes
        // recorded" — an audit page telling a reviewer nothing happened when
        // in fact it could not look.
        <div className="space-y-2">
          <p className="text-destructive text-sm">{t.loadFailed}</p>
          <Button variant="outline" size="sm" onClick={() => refetch()}>
            {t.tryAgain}
          </Button>
        </div>
      ) : changes.length === 0 ? (
        <p className="text-muted-foreground text-sm">{t.empty}</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t.columns.when}</TableHead>
              <TableHead>{t.columns.client}</TableHead>
              <TableHead>{t.columns.field}</TableHead>
              <TableHead>{t.columns.from}</TableHead>
              <TableHead>{t.columns.to}</TableHead>
              <TableHead>{t.columns.source}</TableHead>
              <TableHead>{t.columns.by}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {changes.map((change) => (
              <ChangeRow key={change.id} change={change} />
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
